export type SmsEncoding = 'GSM-7' | 'UCS-2';

export interface SmsCount {
  chars: number;
  segments: number;
  encoding: SmsEncoding;
}

const GSM_BASIC = new Set(
  Array.from(
    '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà',
  ),
);

const GSM_EXTENDED = new Set(Array.from('\f^{}\\[~]|€'));

const SINGLE_LIMIT: Record<SmsEncoding, number> = {
  'GSM-7': 160,
  'UCS-2': 70,
};

const PART_LIMIT: Record<SmsEncoding, number> = {
  'GSM-7': 153,
  'UCS-2': 67,
};

const isGsm = (character: string): boolean =>
  GSM_BASIC.has(character) || GSM_EXTENDED.has(character);

const gsmSize = (character: string): number =>
  GSM_EXTENDED.has(character) ? 2 : 1;

const countSegments = (sizes: number[], encoding: SmsEncoding): number => {
  const total = sizes.reduce((sum, size) => sum + size, 0);
  if (total === 0) {
    return 0;
  }
  if (total <= SINGLE_LIMIT[encoding]) {
    return 1;
  }
  const limit = PART_LIMIT[encoding];
  return sizes.reduce(
    (packed, size) =>
      packed.used + size > limit
        ? { count: packed.count + 1, used: size }
        : { count: packed.count, used: packed.used + size },
    { count: 1, used: 0 },
  ).count;
};

export const smsSegments = (text: string): SmsCount => {
  const characters = Array.from(text);
  const encoding: SmsEncoding = characters.every(isGsm) ? 'GSM-7' : 'UCS-2';
  const sizes = characters.map(character =>
    encoding === 'GSM-7' ? gsmSize(character) : character.length,
  );
  return {
    chars: characters.length,
    segments: countSegments(sizes, encoding),
    encoding,
  };
};
